import type { ReactNode } from 'react';

import { motion } from 'framer-motion';
import { useRouterState } from '@tanstack/react-router';

import { motion as motionTokens } from '@nexus/ari-lib';

interface RouteTransitionProps {
  children: ReactNode;
}

/**
 * Subtle, fast, opacity-led page transition for the persistent layout's <Outlet>.
 *
 * Keyed on the leaf match id (not the URL). TanStack keeps the *old* matches
 * mounted while a lazy chunk loads and only swaps `state.matches` once the new
 * route is ready — so the match id changes atomically with the Outlet content.
 * Keying on `location` instead would flip the key the instant you navigate (before
 * the chunk loads) and replay the old page's animation; keying on
 * `resolvedLocation` lags the content by a layoutEffect and double-mounts. The
 * match id flips exactly when the new content commits → one clean animation, with
 * no flicker whether or not the route was preloaded. Reduced motion is honored by
 * the app-root `<MotionConfig reducedMotion="user">`.
 */
export function RouteTransition({ children }: RouteTransitionProps) {
  const matchId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.id ?? '',
  });
  return (
    <motion.div
      key={matchId}
      initial={{ opacity: 0, y: 8, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: motionTokens.durationMs.base / 1000, ease: motionTokens.easeArray.out }}
    >
      {children}
    </motion.div>
  );
}
